import { useTranslation } from "react-i18next";
import { formatMoneyKzt } from "@/shared/utils";

interface Props {
  paidPercent: number;
  approvedPercent: number;
  contractAmount: number;
  locale: "ru" | "kk" | "en";
}

const SIZE = 210;
const STROKE_WIDTH = 16;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function ContractProgressCircle({ paidPercent, approvedPercent, contractAmount, locale }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const clampedPaid = Math.min(100, Math.max(0, paidPercent));
  const clampedApproved = Math.min(100 - clampedPaid, Math.max(0, approvedPercent));
  const totalPercent = clampedPaid + clampedApproved;

  const paidOffset = CIRCUMFERENCE - (clampedPaid / 100) * CIRCUMFERENCE;
  const approvedArc = (clampedApproved / 100) * CIRCUMFERENCE;
  const approvedOffset = CIRCUMFERENCE - approvedArc;
  const approvedRotation = (clampedPaid / 100) * 360;

  return (
    <div className="relative flex items-center justify-center" style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="-rotate-90">
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="var(--color-grey-100)"
          strokeWidth={STROKE_WIDTH}
        />
        {clampedPaid > 0 && (
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="var(--color-brand-500)"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={paidOffset}
            className="duration-500"
          />
        )}
        {clampedApproved > 0 && (
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="var(--color-orange-500)"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={approvedOffset}
            className="duration-500"
            style={{ transform: `rotate(${approvedRotation}deg)`, transformOrigin: "center" }}
          />
        )}
      </svg>
      <div className="absolute flex flex-col items-center gap-0.5">
        <span className="text-display-sm content-base-primary">{Math.round(totalPercent)}%</span>
        <span className="text-body-regular-xs content-action-neutral">{t("calculations.dashboard.completed")}</span>
        <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(contractAmount, locale)}</span>
      </div>
    </div>
  );
}

